import { endPoint } from '@/constant/end-point'
import { isFeatureAllowedByOrganisationModules } from '@/constant/organisation-module-features'
import { Feature } from '@/enum/global.enum'
import { useActiveOrg } from '@/hooks/use-active-org'
import useAPI from '@/hooks/use-api'
import type { IModule } from '@/interface/module.interface'
import { isOrgRole } from '@/utils/role-check.utils'
import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'

/**
 * Fetches the list of modules enabled for the active organisation (onboarding selection).
 * Only runs for org users with an active org.
 */
export function useOrganisationModules() {
  const { activeOrg } = useActiveOrg()
  const { getOne } = useAPI<IModule[]>()

  const isOrgUser = activeOrg?.role && isOrgRole(activeOrg.role)
  const organisationId = activeOrg?.id

  const { data, isLoading } = useQuery({
    queryKey: ['organisation-modules', organisationId],
    queryFn: () => getOne(`${endPoint.organisation.module.organisationModule}/list?active=true`),
    enabled: !!isOrgUser && !!organisationId,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
  })

  const enabledModuleNames = useMemo(() => {
    if (!data?.data?.length) return []
    return data.data.map((m) => (m.name ?? '').toLowerCase()).filter(Boolean)
  }, [data])

  const isFeatureEnabledForOrg = useMemo(
    () => (feature: Feature | undefined) => isFeatureAllowedByOrganisationModules(feature, enabledModuleNames),
    [enabledModuleNames]
  )

  const showGovernanceTab = [Feature.POLICY, Feature.PROJECT, Feature.SERVICE_PROVIDER].some((feature) =>
    isFeatureEnabledForOrg(feature)
  )

  return {
    enabledModuleNames,
    isFeatureEnabledForOrg,
    showGovernanceTab,
    isLoading: isOrgUser ? isLoading : false,
  }
}
